import { usePagination } from '../lib/usePagination'

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (p: number) => void
}

/**
 * Numeric pagination with first/last + ellipsis. Desktop shows numbers,
 * mobile collapses to prev / "X / Y" / next. `usePagination` controls
 * the window of visible pages; we additionally suppress an ellipsis
 * when the adjacent endpoint is already in `pages` (the hook returns
 * the "should fit a separator" hint, not the final layout decision).
 */
export function Pagination({ currentPage, totalPages, onPageChange }: PaginationProps) {
  const { pages, showLeftEllipsis, showRightEllipsis } = usePagination({
    currentPage,
    totalPages,
    paginationItemsToDisplay: 7,
  })

  if (totalPages <= 1) return null

  const firstVisible = pages[0]
  const lastVisible = pages[pages.length - 1]
  const renderFirst = firstVisible !== 1
  const renderLast = lastVisible !== totalPages
  const renderLeftDots = showLeftEllipsis && renderFirst
  const renderRightDots = showRightEllipsis && renderLast

  const baseBtn =
    'min-w-[2rem] h-8 px-2 rounded-lg text-sm font-medium border transition-colors'
  const inactive =
    'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
  const active = 'border-blue-600 bg-blue-600 text-white'
  const navBtn =
    'h-8 px-3 rounded-lg text-sm font-medium border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-colors'

  const prev = () => currentPage > 1 && onPageChange(currentPage - 1)
  const next = () => currentPage < totalPages && onPageChange(currentPage + 1)

  return (
    <nav className="flex items-center justify-center gap-1.5" aria-label="Pagination">
      <button
        type="button"
        onClick={prev}
        disabled={currentPage <= 1}
        className={navBtn}
        data-testid="pagination-prev"
        aria-label="Предыдущая страница"
      >
        ←
      </button>

      {/* Mobile: simple X / Y */}
      <span className="md:hidden text-sm text-gray-500 dark:text-gray-400 px-2 tabular-nums">
        {currentPage} / {totalPages}
      </span>

      {/* Desktop: numbered pages with ellipsis */}
      <div className="hidden md:flex items-center gap-1.5">
        {renderFirst && (
          <button
            type="button"
            onClick={() => onPageChange(1)}
            className={`${baseBtn} ${currentPage === 1 ? active : inactive}`}
            data-testid="pagination-page-1"
          >
            1
          </button>
        )}
        {renderLeftDots && (
          <span
            className="px-1 text-gray-400 dark:text-gray-600 select-none"
            data-testid="pagination-ellipsis-left"
            aria-hidden="true"
          >
            …
          </span>
        )}
        {pages.map(p => (
          <button
            key={p}
            type="button"
            onClick={() => onPageChange(p)}
            className={`${baseBtn} ${p === currentPage ? active : inactive}`}
            data-testid={`pagination-page-${p}`}
            aria-current={p === currentPage ? 'page' : undefined}
          >
            {p}
          </button>
        ))}
        {renderRightDots && (
          <span
            className="px-1 text-gray-400 dark:text-gray-600 select-none"
            data-testid="pagination-ellipsis-right"
            aria-hidden="true"
          >
            …
          </span>
        )}
        {renderLast && (
          <button
            type="button"
            onClick={() => onPageChange(totalPages)}
            className={`${baseBtn} ${currentPage === totalPages ? active : inactive}`}
            data-testid={`pagination-page-${totalPages}`}
          >
            {totalPages}
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={next}
        disabled={currentPage >= totalPages}
        className={navBtn}
        data-testid="pagination-next"
        aria-label="Следующая страница"
      >
        →
      </button>
    </nav>
  )
}
